/**
 * The authority-matrix route table (design §5.9).
 *
 * `src/router/index.ts` is P9's, so this module declares its records and the
 * router spreads `AUTHORITY_ROUTES` in — the shape `TAX_SPINE_ROUTES` already
 * uses.
 *
 * Every route is gated on a **read** permission. Editing a rule, activating a
 * version, creating a delegation and setting a materiality threshold are all
 * checked on the action, never on the route: an Auditor holds the read codes and
 * nothing else, and must be able to open every one of these screens and change
 * none of them. Gating a screen on `authority.matrix.edit` would make the audit
 * impossible while looking like security.
 *
 * `:id` is constrained to digits so a future `/authority/matrix/new` could never
 * be parsed as a version id. There is deliberately no such route: a version is
 * created from a dialog on the list, which keeps the versions you are cloning
 * from on screen.
 */

import type { RouteRecordRaw } from 'vue-router'

import { AUTHORITY_PERMISSIONS } from '@/modules/authority/permissions'

export const AUTHORITY_ROUTES: RouteRecordRaw[] = [
  {
    path: '/authority/matrix',
    name: 'authority-matrix',
    component: () => import('@/modules/authority/MatrixVersionsView.vue'),
    meta: {
      titleKey: 'authority.routeTitles.matrix',
      permission: AUTHORITY_PERMISSIONS.matrixRead,
    },
  },
  {
    path: '/authority/matrix/:id(\\d+)',
    name: 'authority-matrix-version',
    component: () => import('@/modules/authority/MatrixEditor.vue'),
    meta: {
      titleKey: 'authority.routeTitles.version',
      permission: AUTHORITY_PERMISSIONS.matrixRead,
    },
  },
  {
    path: '/authority/delegations',
    name: 'authority-delegations',
    component: () => import('@/modules/authority/DelegationsView.vue'),
    meta: {
      titleKey: 'authority.routeTitles.delegations',
      permission: AUTHORITY_PERMISSIONS.delegationRead,
    },
  },
  {
    path: '/authority/absences',
    name: 'authority-absences',
    component: () => import('@/modules/authority/AbsencesView.vue'),
    meta: {
      titleKey: 'authority.routeTitles.absences',
      permission: AUTHORITY_PERMISSIONS.absenceRead,
    },
  },
  {
    path: '/authority/materiality',
    name: 'authority-materiality',
    component: () => import('@/modules/authority/MaterialityView.vue'),
    meta: {
      titleKey: 'authority.routeTitles.materiality',
      permission: AUTHORITY_PERMISSIONS.matrixRead,
    },
  },
]
